import 'dayjs/locale/en-gb';

import { useCheckAlive, useGetLocales, useLogin } from 'hooks/login.hook';
import { useEffect, useState } from 'react';
import { Navigate, Route, Routes } from 'react-router-dom';
import {
	Dashboard,
	ViewBenefits,
	ViewBilling,
	ViewCarriers,
	ViewClaimDetails,
	ViewClaimReview,
	ViewClaims,
	ViewClients,
	ViewComplaintTracking,
	ViewComplaintTrackingDetail,
	ViewFraudTracking,
	ViewFraudTrackingDetail,
	ViewGroups,
	ViewMembers,
	ViewPlans,
	ViewProviderNetworks,
	ViewReporting,
	ViewUsers,
} from '../pages';

import { AuthenticatedTemplate } from '@azure/msal-react';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import SharedLayout from 'components/Layout//SharedLayout/SharedLayout.component';
import DashboardLayout from 'components/Layout/DashboardLayout.component';
import DialogConfirm from 'components/commons/Dialogs/DialogConfirm';
import DialogDownloadOther from 'components/commons/Dialogs/downloadOther/DialogDownloadOther';
import DocumentVisualizerPopup from 'components/commons/FileRenderer/DocumentVisualizerPopup';
import { ViewSettlementLetters } from 'pages/ViewSettlementLetters.page';

interface Props {
	className?: string;
}

export const Router: React.FC<Props> = () => {
	const [dateJsStarted, setDatejsStarted] = useState(false);

	//login
	useLogin();

	// check connection
	useCheckAlive();

	// check connection
	useGetLocales();

	useEffect(() => {
		setDatejsStarted(true);
	}, []);

	return (
		<AuthenticatedTemplate>
			<LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale={'en-gb'}>
				<DialogConfirm />
				<DialogDownloadOther />
				<DocumentVisualizerPopup />
				{!dateJsStarted ? null : (
					<Routes>
						<Route path="/" element={<DashboardLayout />}>
							<Route path="" element={<Dashboard />} />
						</Route>
						<Route path="/view-claims" element={<SharedLayout />}>
							<Route path="/view-claims" element={<ViewClaims />} />
							<Route path="/view-claims/:uuid" element={<ViewClaimDetails />} />
							<Route path="/view-claims/:uuid/review" element={<ViewClaimReview />} />
							<Route
								path="/view-claims/:claimUUID/view-complaint/:complaintId"
								element={<ViewComplaintTrackingDetail isComplaintDetails={true} />}
							/>
							<Route
								path="/view-claims/:claimUUID/add-complaint"
								element={<ViewComplaintTrackingDetail isComplaintDetails={false} />}
							/>
							<Route
								path="/view-claims/:claimUUID/view-fraud/:fraudId"
								element={<ViewFraudTrackingDetail isFraudDetails={true} />}
							/>
							<Route
								path="/view-claims/:claimUUID/add-fraud"
								element={<ViewFraudTrackingDetail isFraudDetails={false} />}
							/>
						</Route>
						<Route path="/view-clients" element={<SharedLayout />}>
							<Route path="/view-clients" element={<ViewClients />} />
						</Route>
						<Route path="/view-carriers" element={<SharedLayout />}>
							<Route path="/view-carriers" element={<ViewCarriers />} />
						</Route>
						<Route path="/view-groups" element={<SharedLayout />}>
							<Route path="/view-groups" element={<ViewGroups />} />
						</Route>
						<Route path="/view-providernetworks" element={<SharedLayout />}>
							<Route
								path="/view-providernetworks"
								element={<ViewProviderNetworks />}
							/>
						</Route>
						<Route path="/view-members" element={<SharedLayout />}>
							<Route path="/view-members" element={<ViewMembers />} />
						</Route>
						<Route path="/view-plans" element={<SharedLayout />}>
							<Route path="/view-plans" element={<ViewPlans />} />
						</Route>
						<Route path="/view-benefits" element={<SharedLayout />}>
							<Route path="/view-benefits" element={<ViewBenefits />} />
						</Route>
						<Route path="/view-billing" element={<SharedLayout />}>
							<Route path="/view-billing" element={<ViewBilling />} />
							<Route path="/view-billing/members" element={<ViewBilling />} />
						</Route>
						<Route path="/view-settlement-letters" element={<SharedLayout />}>
							<Route
								path="/view-settlement-letters/:uuid?"
								element={<ViewSettlementLetters />}
							/>
						</Route>
						<Route path="/view-users" element={<SharedLayout />}>
							<Route path="/view-users" element={<ViewUsers />} />
						</Route>
						<Route path="/view-reporting" element={<SharedLayout />}>
							<Route path="/view-reporting" element={<ViewReporting />} />
						</Route>
						<Route path="/view-complaint-tracking" element={<SharedLayout />}>
							<Route
								path="/view-complaint-tracking"
								element={<ViewComplaintTracking />}
							/>
							<Route
								path="/view-complaint-tracking/:complaintId"
								element={<ViewComplaintTrackingDetail isComplaintDetails={true} />}
							/>
						</Route>
						<Route path="/view-fraud-tracking" element={<SharedLayout />}>
							<Route path="/view-fraud-tracking" element={<ViewFraudTracking />} />
							<Route
								path="/view-fraud-tracking/:fraudId"
								element={<ViewFraudTrackingDetail isFraudDetails={true} />}
							/>
						</Route>

						<Route path="*" element={<Navigate to="/not-found" />} />
					</Routes>
				)}
			</LocalizationProvider>
		</AuthenticatedTemplate>
	);
};
